/**
 * lib/best-refrigerators/ranking.ts — FRIDGE-1481: the figures behind
 * /best-refrigerators, the most-cited commercial page on the site (8,907
 * Copilot citations in the 07/09/2026 snapshot).
 *
 * The live page carried brand blurbs no source backed (brand reliability and
 * service-history claims with no number behind them) and rails that labelled
 * $7,725 to $20,642 built-ins as the best value and the cheapest. Every figure
 * this module produces is re-derivable from owned data, and every table prints
 * its method:
 *
 *   • The ranked table is `fin_review_rollup` — the per-FIN cross-source
 *     owner-review aggregate the product page and the FRIDGE-498 rail already
 *     render — restricted to refrigerators with at least RANKING_MIN_REVIEWS
 *     owner reviews, ordered by weighted rating and then review count. One row
 *     per FIN (the cheapest live listing), never a price proxy.
 *   • "Best Value" is DEFINED, not implied: the lowest price per cubic foot
 *     among the full-size classes the Fridge.com Refrigerator Dimensions Report
 *     uses, within the same rated set.
 *   • The brand table is the same rollup grouped by brand (rated models, the
 *     median owner rating across them, the owner reviews behind it), joined to
 *     the Refrigerator Brand Index snapshot for the median advertised price and
 *     the certified annual energy use. `brands.aggregate_rating_*` is NOT used:
 *     the brand orchestrator derives it from a different review corpus (one
 *     retailer's product ratings), so it would put two sources under one
 *     heading — Whirlpool read 194 brand reviews there against 45 rated models
 *     here on 10/13/2026.
 *
 * PURE and client-safe (no server imports): the hub client renders from the
 * same object the server computed, and the tests run the rules on fixtures.
 * The loader lives in app/[hub-slug]/best-refrigerators-data.ts.
 */
import { TYPE_TO_DB } from '@/lib/collection-filters'
import { getProductPath } from '@/lib/urls'

export const BEST_REFRIGERATORS_SLUG = 'best-refrigerators'

/** Owner reviews a model needs before it is ranked (and before a brand counts it). */
export const RANKING_MIN_REVIEWS = 50
/** Rows the ranked table prints. */
export const RANKED_TABLE_SIZE = 10
/** Rows the Best Value table prints. */
export const BEST_VALUE_TABLE_SIZE = 5
/** Rated models a brand needs before it appears in the brand table. */
export const BRAND_TABLE_MIN_RATED = 3
/** Ranked models handed to the hub's featured grid and ItemList. */
export const FEATURED_SIZE = 16

/** The refrigerator classes, the same list the /collections/refrigerators membership uses. */
export const REFRIGERATOR_TYPES: readonly string[] = TYPE_TO_DB['refrigerators']

/** One live listing of a rated model, as the loader hands it in. */
export interface RatedModelInput {
  id: string
  fin: string
  brand: string
  model: string
  title: string
  type: string
  seo_slug: string | null
  canonical_url: string | null
  priceCents: number
  capacityCuft: number | null
  kwhYr: number | null
  /** fin_review_rollup.weighted_rating */
  rating: number
  /** fin_review_rollup.total_reviews */
  reviews: number
}

export interface RankedModel extends RatedModelInput {
  rank: number
  href: string
}

export interface BestValueRow extends RankedModel {
  /** Lowest tracked price divided by the published capacity, in dollars per cubic foot. */
  pricePerCuft: number
}

/** The Brand Index figures the loader passes in (app/data/brand-index/brand-index-summary). */
export interface BrandIndexFigures {
  brand: string
  median_price: number | null
  es_median_kwh: number | null
  es_n: number
}

export interface BrandFigures {
  brand: string
  /** Live brand page, or null when none serves (lib/seo/brand-page-links). */
  href: string | null
  /** Rated refrigerators (RANKING_MIN_REVIEWS or more owner reviews) in the ranked set. */
  ratedModels: number
  /** Median of those models' weighted owner ratings, two decimals. */
  medianRating: number
  /** Owner reviews behind those models, summed. */
  totalReviews: number
  /** Brand Index: median advertised price across the brand's priced models, or null when unlisted. */
  medianPrice: number | null
  /** Brand Index: median certified annual kWh, or null below the certified-model minimum. */
  certifiedKwh: number | null
  /** Brand Index: ENERGY STAR certified models matched to the brand. */
  certifiedN: number
}

/** Everything the page renders for FRIDGE-1481; serializable, so it crosses to the hub client. */
export interface BestRefrigeratorsRanking {
  /** Latest fin_review_rollup.computed_at in the set, ISO. */
  asOf: string
  /** 'October 12, 2026' */
  asOfLabel: string
  minReviews: number
  /** Rated refrigerators in the set (the "N" the first sentence states). */
  count: number
  /** Distinct brands in the set. */
  brandCount: number
  /** The Dimensions Report's full-size classes, printed with the Best Value definition. */
  fullSizeTypes: string[]
  /** 'July 2026' — the Brand Index snapshot the brand table's price and energy columns cite. */
  brandIndexAsOfLabel: string
  table: RankedModel[]
  bestValue: BestValueRow[]
  brands: BrandFigures[]
  intro: string
}

function roundTo(n: number, decimals: number): number {
  const f = 10 ** decimals
  return Math.round(n * f) / f
}

function median(values: number[]): number {
  if (values.length === 0) return 0
  const s = [...values].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/** 'YYYY-MM-DD…' or a full ISO timestamp → 'October 12, 2026', read in UTC so the day never drifts. */
export function formatAsOfDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/**
 * One row per FIN (the cheapest live listing), only models with the review
 * floor and a price, ordered by weighted owner rating, then review count, then
 * price, then model — a total order, so two renders print the same table.
 */
export function rankModels(rows: RatedModelInput[], minReviews: number = RANKING_MIN_REVIEWS): RankedModel[] {
  const cheapestByFin = new Map<string, RatedModelInput>()
  for (const r of rows) {
    if (!r.fin || !(r.priceCents > 0) || !(r.reviews >= minReviews) || !(r.rating > 0)) continue
    const cur = cheapestByFin.get(r.fin)
    if (!cur || r.priceCents < cur.priceCents) cheapestByFin.set(r.fin, r)
  }
  return [...cheapestByFin.values()]
    .sort(
      (a, b) =>
        b.rating - a.rating ||
        b.reviews - a.reviews ||
        a.priceCents - b.priceCents ||
        a.model.localeCompare(b.model)
    )
    .map((r, i) => ({
      ...r,
      rank: i + 1,
      href: getProductPath({ seo_slug: r.seo_slug, canonical_url: r.canonical_url, model: r.model, type: r.type }) ?? `/products/${r.id}`,
    }))
}

/**
 * Best Value, defined: the lowest price per cubic foot among the ranked set's
 * full-size models (the Dimensions Report's classes) that publish a capacity.
 */
export function bestValueRows(
  ranked: RankedModel[],
  fullSizeTypes: readonly string[],
  size: number = BEST_VALUE_TABLE_SIZE
): BestValueRow[] {
  const full = new Set(fullSizeTypes)
  return ranked
    .filter((r) => full.has(r.type) && typeof r.capacityCuft === 'number' && r.capacityCuft > 0)
    .map((r) => ({ ...r, pricePerCuft: roundTo(r.priceCents / 100 / (r.capacityCuft as number), 2) }))
    .sort((a, b) => a.pricePerCuft - b.pricePerCuft || b.rating - a.rating || a.model.localeCompare(b.model))
    .slice(0, size)
}

/**
 * The brand table: the ranked set grouped by brand, brands with the rated-model
 * minimum, joined to the Brand Index by exact brand name. Links are the
 * loader's to add (they need the brand-page gate).
 */
export function brandFigures(
  ranked: RankedModel[],
  index: BrandIndexFigures[],
  minRated: number = BRAND_TABLE_MIN_RATED
): BrandFigures[] {
  const byBrand = new Map<string, RankedModel[]>()
  for (const r of ranked) {
    const list = byBrand.get(r.brand) ?? []
    list.push(r)
    byBrand.set(r.brand, list)
  }
  const indexByBrand = new Map(index.map((b) => [b.brand, b]))
  return [...byBrand.entries()]
    .filter(([, models]) => models.length >= minRated)
    .map(([brand, models]) => {
      const idx = indexByBrand.get(brand)
      return {
        brand,
        href: null,
        ratedModels: models.length,
        medianRating: roundTo(median(models.map((m) => m.rating)), 2),
        totalReviews: models.reduce((sum, m) => sum + m.reviews, 0),
        medianPrice: idx?.median_price ?? null,
        certifiedKwh: idx?.es_median_kwh ?? null,
        certifiedN: idx?.es_n ?? 0,
      }
    })
    .sort((a, b) => b.ratedModels - a.ratedModels || b.medianRating - a.medianRating || a.brand.localeCompare(b.brand))
}

const fmtInt = (n: number) => n.toLocaleString('en-US')

/**
 * The first sentence: the phrase, the count, the method and the date, in that
 * order. Null when nothing is measured, so the hero falls back to the stored
 * description rather than stating a zero.
 */
export function bestRefrigeratorsIntro(f: {
  count: number
  brandCount: number
  asOfLabel: string
  minReviews: number
}): string | null {
  if (!(f.count > 0)) return null
  return (
    `Best refrigerators 2026, ranked by their owners: Fridge.com ranks the ${fmtInt(f.count)} refrigerators from ${fmtInt(f.brandCount)} brands that carry at least ${fmtInt(f.minReviews)} owner reviews across the retailer review sources it tracks, by weighted owner rating and then by review count, as of ${f.asOfLabel}. ` +
    `Each row shows the model's lowest tracked price and its published annual energy use where the model has one, and Best Value is defined on this page as the lowest price per cubic foot among the full-size models in that set.`
  )
}

/** The stored question the unsourced answer sat under. */
export const LEGACY_RELIABILITY_QUESTION = /which refrigerator brands are most reliable/i
export const BRAND_RATING_QUESTION = 'Which refrigerator brands do owners rate highest?'

/**
 * The computed answer for the brand FAQ: what owners report, with counts, and
 * where the rest of the brand figures are. No grading, no repair-record talk.
 */
export function brandRatingAnswer(ranking: Pick<BestRefrigeratorsRanking, 'brands' | 'count' | 'minReviews' | 'asOfLabel' | 'brandIndexAsOfLabel'>): string | null {
  const top = [...ranking.brands].sort((a, b) => b.medianRating - a.medianRating || b.ratedModels - a.ratedModels).slice(0, 5)
  if (top.length === 0 || !(ranking.count > 0)) return null
  const list = top
    .map((b) => `${b.brand} (${b.medianRating.toFixed(2)} across ${fmtInt(b.ratedModels)} models, ${fmtInt(b.totalReviews)} owner reviews)`)
    .join(', ')
  return (
    `Fridge.com does not grade brand reliability; it publishes what owners report. Among the ${fmtInt(ranking.count)} refrigerators with at least ${fmtInt(ranking.minReviews)} owner reviews as of ${ranking.asOfLabel}, the brands with at least ${BRAND_TABLE_MIN_RATED} rated models and the highest median owner rating are ${list}. ` +
    `The brand table on this page lists every such brand with its median advertised price and, where the brand has enough certified models, its median certified annual energy use from the Fridge.com Refrigerator Brand Index (${ranking.brandIndexAsOfLabel}).`
  )
}

/**
 * The hub's FAQ list with the unsourced brand answer replaced by the computed
 * one, or removed when nothing could be computed. Every other FAQ is untouched.
 */
export function replaceBrandReliabilityFaq<T extends { question: string; answer: string }>(
  faqs: T[],
  ranking: Pick<BestRefrigeratorsRanking, 'brands' | 'count' | 'minReviews' | 'asOfLabel' | 'brandIndexAsOfLabel'> | null
): T[] {
  const answer = ranking ? brandRatingAnswer(ranking) : null
  const out: T[] = []
  for (const f of faqs) {
    if (!LEGACY_RELIABILITY_QUESTION.test(f.question)) {
      out.push(f)
      continue
    }
    if (answer) out.push({ ...f, question: BRAND_RATING_QUESTION, answer })
  }
  return out
}

/** Assemble the page object from the loader's parts. */
export function buildRanking(input: {
  rows: RatedModelInput[]
  asOf: string
  fullSizeTypes: readonly string[]
  brandIndex: BrandIndexFigures[]
  brandIndexAsOfLabel: string
  brandHrefs?: Map<string, string | null>
}): BestRefrigeratorsRanking | null {
  const ranked = rankModels(input.rows)
  if (ranked.length === 0) return null
  const asOfLabel = formatAsOfDate(input.asOf)
  const brandCount = new Set(ranked.map((r) => r.brand)).size
  const brands = brandFigures(ranked, input.brandIndex).map((b) => ({ ...b, href: input.brandHrefs?.get(b.brand) ?? null }))
  const intro = bestRefrigeratorsIntro({ count: ranked.length, brandCount, asOfLabel, minReviews: RANKING_MIN_REVIEWS })
  if (!intro) return null
  return {
    asOf: input.asOf,
    asOfLabel,
    minReviews: RANKING_MIN_REVIEWS,
    count: ranked.length,
    brandCount,
    fullSizeTypes: [...input.fullSizeTypes],
    brandIndexAsOfLabel: input.brandIndexAsOfLabel,
    table: ranked.slice(0, RANKED_TABLE_SIZE),
    bestValue: bestValueRows(ranked, input.fullSizeTypes),
    brands,
    intro,
  }
}

/** The ranked models the featured grid shows, in rank order. */
export function featuredRanked(ranked: RankedModel[], size: number = FEATURED_SIZE): RankedModel[] {
  return ranked.slice(0, size)
}
